import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import React from 'react';

import { RoleR } from '../../../services/roles';
import { AUTH_METHOD_LABELS, UserR } from '../../../services/users';
import { FileUpload } from '../../../utils/fileUpload';
import { generateRoleOptions } from '../../../utils/options';

interface UserFormFieldsProps {
  user?: UserR;
  roles: RoleR[];
  loadingRoles: boolean;
}

// Accepts exact hosts (`app.example.com`), wildcard subdomains (`*.example.com`)
// and single labels like `localhost`. Rejects schemes, paths, ports and spaces.
const REDIRECT_DOMAIN_REGEX = /^(\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;

const AUTH_METHOD_OPTIONS = Object.entries(AUTH_METHOD_LABELS).map(([value, label]) => ({ value, label }));

export const UserFormFields: React.FC<UserFormFieldsProps> = ({ user, roles, loadingRoles }) => {
  // Memoized role options
  const roleOptions = React.useMemo(() => {
    if (!user?.roles) {
      return generateRoleOptions(roles);
    }

    const userRoleMappings = user.roles.map((el) => ({
      roleId: el.role.id,
      condition: el.condition
    }));

    return generateRoleOptions(roles, userRoleMappings);
  }, [roles, user?.roles]);

  return (
    <>
      <Row gutter={32}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: 'Please input email!'
              },
              {
                type: 'email',
                message: 'Please use correct email format!'
              },
              {
                max: 250,
                message: 'Email max length is 250!'
              }
            ]}
          >
            <Input placeholder="Email" disabled={!!user} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="firstName"
            label="First name"
            rules={[
              {
                required: true,
                message: 'Please input first name!'
              },
              {
                max: 250,
                message: 'First name max length is 250!'
              }
            ]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="lastName"
            label="Last name"
            rules={[
              {
                required: true,
                message: 'Please input last name!'
              },
              {
                max: 250,
                message: 'Last name max length is 250!'
              }
            ]}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="legalEmail" label="Legal Email">
            <Input placeholder="Legal Email" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={32}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="authType" label="Authentication method">
            <Select
              placeholder="Select authentication method"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              virtual={false}
            >
              <Select.Option value={null}>Not selected</Select.Option>
              <Select.Option value="password">Password</Select.Option>
              <Select.Option value="sso">SSO (Google or Facebook)</Select.Option>
              {/* Derived at login (password user signs in with SSO), never assignable —
                  listed only so existing accounts render a label instead of "both". */}
              <Select.Option value="both" disabled>
                Password + SSO
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item label="Sign-in methods in use">
            {/* Same control as Roles so the two read alike, but inert: methods are
                established when the user signs in, never assigned by an admin. */}
            <Select
              mode="multiple"
              open={false}
              showSearch={false}
              removeIcon={null}
              value={user?.authMethods ?? []}
              options={AUTH_METHOD_OPTIONS}
              placeholder="None yet — setup pending"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="slackId" label="Slack ID">
            <Input placeholder="Slack ID" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="roles" label="Roles">
            <Select
              placeholder="Roles"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="data-searchvalue"
              loading={loadingRoles}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              virtual={false}
            >
              {roleOptions}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="avatar">
            <FileUpload />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={32}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Allowed redirect domains"
            tooltip="Whitelist of domains this user (as a client) may use as redirect_url when generating an auth URL. Use exact hosts (app.example.com) or wildcards (*.example.com). Leave empty to disallow all redirects."
          >
            <Form.List name="allowedRedirectDomains">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Form.Item key={field.key} style={{ marginBottom: 12 }}>
                      <Input.Group compact>
                        <Form.Item
                          {...field}
                          noStyle
                          validateTrigger={['onChange', 'onBlur']}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: 'Please input a domain or remove this entry.'
                            },
                            {
                              pattern: REDIRECT_DOMAIN_REGEX,
                              message: 'Use a host like app.example.com or *.example.com.'
                            }
                          ]}
                        >
                          <Input
                            placeholder="e.g. app.example.com or *.example.com"
                            style={{ width: 'calc(100% - 32px)' }}
                          />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(field.name)}
                          style={{ width: 32 }}
                        />
                      </Input.Group>
                    </Form.Item>
                  ))}
                  <Form.Item noStyle>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add domain
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
